import React, { useEffect, useRef, ReactElement } from 'react';
import { FiInfo, FiAlertTriangle, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { CustomDialogProps, UseCustomDialogReturn } from './types';

/**
 * CustomDialogのビジネスロジックを扱うカスタムフック
 */
export const useCustomDialog = (props: CustomDialogProps): UseCustomDialogReturn => {
  const { isOpen, onCancel, type = 'info' } = props;
  const dialogRef = useRef<HTMLDivElement>(null);

  // ダイアログがマウントされたら表示アニメーションを開始
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      // フォーカスを確認ボタンに設定（アクセシビリティのため）
      const confirmButton = dialogRef.current.querySelector('.custom-dialog-confirm-button');
      if (confirmButton) {
        (confirmButton as HTMLButtonElement).focus();
      }
    }
  }, [isOpen]);

  // ESCキーでダイアログを閉じる
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  // イベント伝播を止める
  const stopPropagation = (e: React.SyntheticEvent): void => {
    e.stopPropagation();
  };

  // typeに応じた色を設定
  const getColor = (): string => {
    switch (type) {
      case 'warning':
        return '#FFA500';
      case 'success':
        return '#4CAF50';
      case 'error':
        return '#F44336';
      case 'info':
      default:
        return '#2196F3';
    }
  };

  // typeに応じたアイコンを取得
  const getIcon = (): ReactElement => {
    const iconSize = 24;
    const iconColor = getColor();
    
    switch (type) {
      case 'warning':
        return <FiAlertTriangle size={iconSize} color={iconColor} />;
      case 'success':
        return <FiCheckCircle size={iconSize} color={iconColor} />;
      case 'error':
        return <FiXCircle size={iconSize} color={iconColor} />;
      case 'info':
      default:
        return <FiInfo size={iconSize} color={iconColor} />;
    }
  };

  return {
    dialogRef,
    stopPropagation,
    getIcon,
    getColor,
    styles: {} as Record<string, unknown>,
  };
}; 